/**
 * Widget Component Registry (DRR-048)
 *
 * IMPORTANT: This is a COMPONENT-ONLY registry.
 * All metadata (displayName, description, props schema) comes from the backend
 * via the useAvailableWidgets() hook.
 *
 * This file ONLY contains:
 * 1. Component imports and lazy loaders
 * 2. Mapping from widget name to React component
 *
 * Build variants control which components are bundled:
 * - core: Only Mantine components (~50KB)
 * - healthcare: Core + FHIR widgets (~80KB)
 * - financial: Core + Accounting widgets (~85KB)
 * - full: All widgets (~150KB, default for development)
 *
 * Usage:
 *   VITE_BUILD_VARIANT=healthcare pnpm build
 */

import { lazy, type ComponentType, type LazyExoticComponent } from 'react';
import {
  TextInput,
  Textarea,
  NumberInput,
  Select,
  MultiSelect,
  Checkbox,
  Switch,
  FileInput,
  ColorInput,
  PasswordInput,
  JsonInput,
  Rating,
  Slider,
} from '@mantine/core';
import { DateInput, DateTimePicker, TimeInput } from '@mantine/dates';

import type { BaseWidgetProps } from './types';

// =============================================================================
// BUILD VARIANT DETECTION
// =============================================================================

type BuildVariant = 'core' | 'healthcare' | 'financial' | 'full' | 'payroll' | 'inventory';

/**
 * Get the current build variant from environment
 * Defaults to 'full' in development for maximum flexibility
 */
const BUILD_VARIANT: BuildVariant =
  ((import.meta as unknown as { env?: Record<string, string> }).env?.VITE_BUILD_VARIANT as BuildVariant) || 'full';

export { BUILD_VARIANT };

// =============================================================================
// COMPONENT TYPE
// =============================================================================

/** Component that can be either eager or lazy loaded */
export type WidgetComponent =
  | ComponentType<BaseWidgetProps>
  | LazyExoticComponent<ComponentType<BaseWidgetProps>>;

// =============================================================================
// MANTINE ADAPTER
// =============================================================================

/**
 * Adapter to make Mantine components conform to BaseWidgetProps
 */
function createMantineAdapter<P extends Record<string, unknown>>(
  MantineComponent: ComponentType<P>
): ComponentType<BaseWidgetProps> {
  return function MantineWidgetAdapter(props: BaseWidgetProps) {
    const {
      name,
      label,
      value,
      onChange,
      required,
      disabled,
      readOnly,
      placeholder,
      description,
      error,
      componentProps = {},
    } = props;

    const mantineProps = {
      name,
      label,
      value,
      onChange,
      required,
      disabled: disabled || readOnly,
      placeholder,
      description,
      error,
      ...componentProps,
    } as unknown as P;

    return <MantineComponent {...mantineProps} />;
  };
}

/**
 * Lazy load a domain widget
 */
function lazyWidget(
  loader: () => Promise<{ default: ComponentType<BaseWidgetProps> }>
): LazyExoticComponent<ComponentType<BaseWidgetProps>> {
  return lazy(loader);
}

// =============================================================================
// CORE COMPONENT MAP (Always included)
// =============================================================================

const CORE_COMPONENTS: Record<string, WidgetComponent> = {
  TextInput: createMantineAdapter(TextInput),
  Textarea: createMantineAdapter(Textarea),
  NumberInput: createMantineAdapter(NumberInput),
  Select: createMantineAdapter(Select),
  MultiSelect: createMantineAdapter(MultiSelect),
  Checkbox: createMantineAdapter(Checkbox),
  Switch: createMantineAdapter(Switch),
  DateInput: createMantineAdapter(DateInput),
  DateTimePicker: createMantineAdapter(DateTimePicker),
  TimeInput: createMantineAdapter(TimeInput),
  FileInput: createMantineAdapter(FileInput),
  ColorInput: createMantineAdapter(ColorInput),
  PasswordInput: createMantineAdapter(PasswordInput),
  JsonInput: createMantineAdapter(JsonInput),
  Rating: createMantineAdapter(Rating),
  Slider: createMantineAdapter(Slider),
};

// =============================================================================
// COMPLEX FIELD TYPE COMPONENTS (DRR-060 — Always included)
// =============================================================================

const COMPLEX_COMPONENTS: Record<string, WidgetComponent> = {
  COMPUTED: lazyWidget(() => import('./complex/ComputedField')),
  HIERARCHICAL_SELECT: lazyWidget(() => import('./complex/HierarchicalSelect')),
  GRID: lazyWidget(() => import('./complex/GridField')),
  MASTER_DETAIL: lazyWidget(() => import('./complex/MasterDetailSelect')),
};

// =============================================================================
// FHIR COMPONENTS (healthcare and full builds)
// =============================================================================

const FHIR_COMPONENTS: Record<string, WidgetComponent> =
  BUILD_VARIANT === 'healthcare' || BUILD_VARIANT === 'full'
    ? {
        PatientSearch: lazyWidget(() => import('./fhir/PatientSearch')),
        PractitionerSearch: lazyWidget(() => import('./fhir/PractitionerSearch')),
        MedicationSelector: lazyWidget(() => import('./fhir/MedicationSelector')),
        DiagnosisCodePicker: lazyWidget(() => import('./fhir/DiagnosisCodePicker')),
        // ADL Documentation widgets (DRR-056)
        ADLChecklist: lazyWidget(() => import('./fhir/ADLChecklist')),
        EVVCheckIn: lazyWidget(() => import('./fhir/EVVCheckIn')),
        ProcedureCodePicker: lazyWidget(() => import('./fhir/ProcedureCodePicker')),
        VitalSignsInput: lazyWidget(() => import('./fhir/VitalSignsInput')),
      }
    : {};

// =============================================================================
// ACCOUNTING COMPONENTS (financial and full builds)
// =============================================================================

const ACCOUNTING_COMPONENTS: Record<string, WidgetComponent> =
  BUILD_VARIANT === 'financial' || BUILD_VARIANT === 'full'
    ? {
        GlAccountPicker: lazyWidget(() => import('./accounting/GlAccountPicker')),
        JournalLinesEditor: lazyWidget(() => import('./accounting/JournalLinesEditor')),
        VendorSearch: lazyWidget(() => import('./accounting/VendorSearch')),
        CustomerSearch: lazyWidget(() => import('./accounting/CustomerSearch')),
        CurrencyInput: lazyWidget(() => import('./accounting/CurrencyInput')),
        FiscalPeriodSelector: lazyWidget(() => import('./accounting/FiscalPeriodSelector')),
      }
    : {};

// =============================================================================
// PAYROLL COMPONENTS (payroll and full builds)
// =============================================================================

const PAYROLL_COMPONENTS: Record<string, WidgetComponent> =
  BUILD_VARIANT === 'payroll' || BUILD_VARIANT === 'full'
    ? {
        EmployeeSearch: lazyWidget(() => import('./payroll/EmployeeSearch')),
        TimeEntryEditor: lazyWidget(() => import('./payroll/TimeEntryEditor')),
        PayCodeSelect: lazyWidget(() => import('./payroll/PayCodeSelect')),
      }
    : {};

// =============================================================================
// INVENTORY COMPONENTS (inventory and full builds)
// =============================================================================

// TODO: Inventory widgets - use create-widget.sh to scaffold
const INVENTORY_COMPONENTS: Record<string, WidgetComponent> =
  BUILD_VARIANT === 'inventory' || BUILD_VARIANT === 'full' ? {} : {};

// =============================================================================
// COMBINED COMPONENT MAP
// =============================================================================

/**
 * Complete component map for current build variant
 *
 * NOTE: This map ONLY contains components, no metadata.
 * All metadata (displayName, description, props schema) comes from
 * the backend via useAvailableWidgets().
 */
export const WIDGET_COMPONENTS: Record<string, WidgetComponent> = {
  ...CORE_COMPONENTS,
  ...COMPLEX_COMPONENTS,
  ...FHIR_COMPONENTS,
  ...ACCOUNTING_COMPONENTS,
  ...PAYROLL_COMPONENTS,
  ...INVENTORY_COMPONENTS,
};

// =============================================================================
// REGISTRY API
// =============================================================================

/**
 * Get a widget component by name
 *
 * NOTE: This returns the component only. For metadata (displayName, description),
 * use the useAvailableWidgets() hook from the backend.
 */
export function getWidgetComponent(name: string): WidgetComponent | null {
  return WIDGET_COMPONENTS[name] || null;
}

/**
 * Check if a widget component exists in this build
 */
export function hasWidgetComponent(name: string): boolean {
  return name in WIDGET_COMPONENTS;
}

/**
 * Get all available widget names in this build
 */
export function getAvailableWidgetNames(): string[] {
  return Object.keys(WIDGET_COMPONENTS);
}

/**
 * Get build information for debugging
 */
export function getBuildInfo(): {
  variant: BuildVariant;
  componentCount: number;
  availableWidgets: string[];
} {
  return {
    variant: BUILD_VARIANT,
    componentCount: Object.keys(WIDGET_COMPONENTS).length,
    availableWidgets: Object.keys(WIDGET_COMPONENTS),
  };
}

// =============================================================================
// DEPRECATED - For backward compatibility only
// =============================================================================

/**
 * @deprecated Use getWidgetComponent() instead.
 * This function is kept for backward compatibility during migration.
 * It will be removed in a future version.
 */
export function getWidget(type: string): { found: boolean; component?: WidgetComponent; error?: string } {
  const component = WIDGET_COMPONENTS[type];

  if (!component) {
    return {
      found: false,
      error: `Widget component "${type}" not found in registry (build variant: ${BUILD_VARIANT})`,
    };
  }

  return {
    found: true,
    component,
  };
}

/**
 * @deprecated Metadata now comes from backend via useAvailableWidgets().
 * This export is kept for backward compatibility during migration.
 */
export const WIDGET_REGISTRY = WIDGET_COMPONENTS;

/**
 * @deprecated Use hasWidgetComponent() instead.
 */
export const hasWidget = hasWidgetComponent;

/**
 * @deprecated Categories now come from backend via useAvailableWidgets().
 * Returns widget names filtered by category prefix for backward compatibility.
 */
export function getWidgetsByCategory(category: string): string[] {
  const categoryMap: Record<string, string[]> = {
    core: Object.keys(CORE_COMPONENTS),
    complex: Object.keys(COMPLEX_COMPONENTS),
    fhir: Object.keys(FHIR_COMPONENTS),
    accounting: Object.keys(ACCOUNTING_COMPONENTS),
    payroll: Object.keys(PAYROLL_COMPONENTS),
    inventory: Object.keys(INVENTORY_COMPONENTS),
  };
  return categoryMap[category] || [];
}

/**
 * @deprecated Use getAvailableWidgetNames() instead.
 */
export const getAvailableWidgetTypes = getAvailableWidgetNames;

/**
 * @deprecated Metadata now comes from backend via useAvailableWidgets().
 * Returns minimal metadata for backward compatibility.
 */
export function getWidgetMetadata(): Array<{ type: string; category: string }> {
  const result: Array<{ type: string; category: string }> = [];

  Object.keys(CORE_COMPONENTS).forEach(type => result.push({ type, category: 'core' }));
  Object.keys(FHIR_COMPONENTS).forEach(type => result.push({ type, category: 'fhir' }));
  Object.keys(ACCOUNTING_COMPONENTS).forEach(type => result.push({ type, category: 'accounting' }));
  Object.keys(PAYROLL_COMPONENTS).forEach(type => result.push({ type, category: 'payroll' }));
  Object.keys(INVENTORY_COMPONENTS).forEach(type => result.push({ type, category: 'inventory' }));

  return result;
}

/**
 * @deprecated Categories now come from backend.
 */
export function isCategoryAvailable(category: string): boolean {
  return getWidgetsByCategory(category).length > 0;
}

/**
 * @deprecated Categories now come from backend.
 */
export const AVAILABLE_CATEGORIES = ['core', 'fhir', 'accounting', 'payroll', 'inventory'] as const;
